// src/services/api.js
import axios from "axios";
import AuthHelper from "../helpers/auth-helper";
import { renewUserToken } from "./auth-service";
import { LIST_ENDPOINTS, EXCLUDED_ENDPOINTS } from "../config/api-endpoints";

const baseURL =
  process.env.VUE_APP_BACKEND_URL || "http://localhost/mepci-api/public/v1";
const api = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  timeoutErrorMessage: "Network error",
  withCredentials: false,
});

// ============ GESTION DU REFRESH TOKEN ============
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Fonction optimisée pour identifier les endpoints de liste
const isListEndpoint = (url) => {
  const cleanUrl = url.replace(baseURL, "").split("?")[0];

  const isList = Object.values(LIST_ENDPOINTS).some((endpoint) => {
    return (
      cleanUrl === endpoint ||
      (cleanUrl.startsWith(endpoint + "/") && !isNaN(cleanUrl.split("/")[2]))
    );
  });

  const isExcluded = EXCLUDED_ENDPOINTS.some((excluded) =>
    url.includes(excluded),
  );

  return isList && !isExcluded;
};

api.interceptors.request.use((config) => {
  if (!config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }

  if (!config.headers["Accept"]) {
    config.headers["Accept"] = "application/json";
  }

  const token = AuthHelper.getToken();
  if (token && token.length > 0) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.method === "get" && isListEndpoint(config.url)) {
    config.params = config.params || {};
    if (config.params.all === undefined) {
      config.params.all = true;
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Si pas de réponse (erreur réseau)
    if (!error.response) {
      console.log('🌐 Erreur réseau:', error.message);
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const statusCode = error.response.status;
    
    // Log détaillé pour debug
    console.log(`🔴 Erreur ${statusCode} sur ${originalRequest.url}`);
    console.log('📦 Réponse:', error.response.data);

    // ✅ CAS 1: Erreur 500 (serveur) - Pas de refresh
    if (statusCode >= 500) {
      console.log("⚠️ Internal Server Error");
      return Promise.reject(error);
    } 
    
    // ✅ CAS 2: Erreur 401 (non autorisé)
    else if (statusCode === 401) {
      const ignoreAuthResponseCodes = [401100, 401101, 401102];
      const responseCode = error.response.data?.code;
      
      console.log("🔴 401 DÉTECTÉ - Token expiré ou invalide");
      console.log("📦 Token actuel:", AuthHelper.getToken()?.substring(0, 20) + "...");

      // Codes d'erreur à ignorer (pas de refresh)
      if (ignoreAuthResponseCodes.includes(responseCode)) {
        console.log("🚫 Code ignoré, déconnexion");
        AuthHelper.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // Éviter les boucles sur l'endpoint de refresh
      if (originalRequest.url.includes("/auth/token")) {
        console.log("🚫 Échec du refresh token, déconnexion");
        AuthHelper.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // Si déjà en train de rafraîchir, mettre en file d'attente
      if (isRefreshing) {
        console.log("⏳ Refresh en cours, mise en file d'attente");
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Pas de refresh en cours, on lance le processus
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log("🔄 Tentative de refresh token...");
        
        // Appeler le service de refresh
        const data = await renewUserToken();
        
        console.log("✅ REFRESH RÉUSSI !");
        console.log("🔑 Nouveau token:", data.token.substring(0, 20) + "...");

        if (data && data.token) {
          // Sauvegarder les nouveaux tokens
          AuthHelper.saveUserInfo(data);

          // Mettre à jour le header par défaut
          api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;

          // Traiter la file d'attente
          processQueue(null, data.token);

          // Rejouer la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        } else {
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        console.log("❌ ÉCHEC DU REFRESH:", refreshError.message);
        
        // Traiter la file d'attente avec l'erreur
        processQueue(refreshError, null);
        
        // Déconnexion
        AuthHelper.logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } 
    
    // ✅ CAS 3: Erreur 403 (interdit)
    else if (statusCode === 403) {
      console.log("🔒 Permission Denied");
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;